import { Client } from "@microsoft/microsoft-graph-client";
import { isBefore, isValid, startOfYesterday } from "date-fns";
import { WorkbookRange } from "microsoft-graph";
import { DatesAndState, DatesAndChairs, DataIntegrityReport } from "../types";
import { SpecialtyClinicWorksheet } from "./ExcelClinicsService";
import { ParsingResults } from "./excelclinicstypes";
import { getJsDateFromExcel } from "./ExcelClinicUtils";


export async function parseWorksheetFormattedForClosures(
  excelWorksheet: SpecialtyClinicWorksheet,
  ignorePastEvents: boolean,
  graphClient: Client
): Promise<ParsingResults<DatesAndState[]>> {
  let dataIntegrityReport :DataIntegrityReport = {
    URL: excelWorksheet.URI,
    accessedSuccessfully: false,
    errorSummary: undefined,
    possiblyHumanInterperableAmbiguities: []
  }
  try {
    const rangeResponse: WorkbookRange = await graphClient
      .api(`${excelWorksheet.URI}range/usedRange`)
      .get();
    const collumnLetterForStartTime = "A";
    const collumnLetterForState = "B";
    const collumnLetterForMessage = "C";

    if(rangeResponse){
      dataIntegrityReport.accessedSuccessfully = true;
    }

    let startTimeColumnData = graphClient
      .api(
        `${excelWorksheet.URI}range(address='${collumnLetterForStartTime}2:${collumnLetterForStartTime}${rangeResponse.rowCount}')`
      )
      .get();
    let stateColumnData = graphClient
      .api(
        `${excelWorksheet.URI}range(address='${collumnLetterForState}2:${collumnLetterForState}${rangeResponse.rowCount}')`
      )
      .get();
    let messageColumnData = graphClient
      .api(
        `${excelWorksheet.URI}range(address='${collumnLetterForMessage}2:${collumnLetterForMessage}${rangeResponse.rowCount}')`
      )
      .get();

    let formatted_data: DatesAndState[] = await Promise.all([
      startTimeColumnData,
      stateColumnData,
      messageColumnData,
    ]).then((values) => {
      let combined_result: DatesAndState[] = [];
      const dates = values[0].values;
      const state = values[1].text;
      const message = values[2].text;

      dates.forEach((excelDate: number, index: number) => {
        const formattedDate = getJsDateFromExcel(excelDate);
        if (isValid(formattedDate)) {
          if (
            ignorePastEvents === true &&
            isBefore(formattedDate, startOfYesterday())
             //Do nothing
          ) {
          } else {
            combined_result.push({
              dateUTCMilliseconds: formattedDate.getTime(),
              state: state[index][0],
              message: message[index][0],
            });
          }
        }else{
          //try to capture human readable ambiguities
          //blank date means blank to computers and humans
          if(values[0].text[index][0]!==""){
          dataIntegrityReport.errorSummary = "We found some ambiguous data, perhaps you can make sense of it";
          dataIntegrityReport.possiblyHumanInterperableAmbiguities.push(`${values[0].text[index]}\t ${state[index][0]}\t ${message[index][0]} `)
          }
          
        }
      });
      return Promise.resolve(combined_result);
    });
    if(formatted_data.length === 0){
      dataIntegrityReport.errorSummary = `It seems the excel sheet for ${excelWorksheet.clinicEnum} has no future closures.`  
    }
    return Promise.resolve({results: formatted_data , dataIntegrityReport:dataIntegrityReport});
  } catch (e) {
    console.warn(e);
    throw e;
  }
}

export async function parseWorksheetFormattedForOpenings(
  excelWorksheet: SpecialtyClinicWorksheet,
  ignorePastEvents: boolean,
  graphClient: Client
): Promise<ParsingResults<DatesAndChairs[]>> {
  let dataIntegrityReport :DataIntegrityReport = {
    URL: excelWorksheet.URI,
    accessedSuccessfully: false,
    errorSummary: undefined,
    possiblyHumanInterperableAmbiguities: []
  }
  try {
    //The following must be exactly "Start Date Time" formatted as excell date, "CHAIRS LEFT" in capitals and be found within cell range A1:M5
    const collumnLetterForChairsLeft = await getColumLetterWhichisExactly(
      excelWorksheet,
      "CHAIRS LEFT",
      graphClient
    );
    if(collumnLetterForChairsLeft){
      dataIntegrityReport.accessedSuccessfully =true;
    }
    const collumnLetterForStartTime = await getColumLetterWhichisExactly(
      excelWorksheet,
      "Start Date Time",
      graphClient
    );
    if (
      collumnLetterForStartTime === undefined ||
      collumnLetterForChairsLeft === undefined
    ) {
      dataIntegrityReport.errorSummary = `Couldn't locate columns 'Start Date Time' and 'CHAIRS LEFT' in excel, please ensure precice namings`
      return {results:[], dataIntegrityReport:dataIntegrityReport};
    }
    let rangeResponse: WorkbookRange = await graphClient
      .api(`${excelWorksheet.URI}range/usedRange`)
      .get();
    let startTimeColumnData = graphClient
      .api(
        `${excelWorksheet.URI}range(address='${collumnLetterForStartTime}3:${collumnLetterForStartTime}${rangeResponse.rowCount}')`
      )
      .get();
    let openChairsColumnData = graphClient
      .api(
        `${excelWorksheet.URI}range(address='${collumnLetterForChairsLeft}3:${collumnLetterForChairsLeft}${rangeResponse.rowCount}')`
      )
      .get();

    let formatted_data: DatesAndChairs[] = await Promise.all([
      startTimeColumnData,
      openChairsColumnData,
    ]).then((values) => {
      let combined_result: DatesAndChairs[] = [];
      const datesTextFormat= values[0].text 
      const datesExcelNumberFormat = values[0].values;
      const chairs = values[1].text;
      datesExcelNumberFormat.forEach((excelDate: number, index: number) => {
        const formattedDate = getJsDateFromExcel(excelDate);
        if (isValid(formattedDate)) {
          if (
            ignorePastEvents === true &&
            isBefore(formattedDate, startOfYesterday())
            //Do nothing
          ) {
          } else {
            combined_result.push({
              dateUTCMilliseconds: formattedDate.getTime(),
              chairs: parseInt(chairs[index][0]),
            });
          }
        }else{
          //Does it justify being read by a human?

          //No
          if(datesTextFormat[index][0].includes("HOLIDAY")||
          datesTextFormat[index][0].includes("BREAK")||
          datesTextFormat[index][0].trim() === ""){
          }else{
            //Yes
            dataIntegrityReport.possiblyHumanInterperableAmbiguities.push(`Date: ${datesTextFormat[index][0]} | Chairs: ${chairs[index][0]}`)
            dataIntegrityReport.errorSummary = "We found some ambiguous data, perhaps you can make sense of it:"
          }

        }
      });
      return Promise.resolve(combined_result);
    });

    if(formatted_data.length === 0){
      dataIntegrityReport.errorSummary = `It seems the excel sheet for ${excelWorksheet.clinicEnum} has no data. Showing home availability.` 
    }
    
    return {results: formatted_data, dataIntegrityReport:dataIntegrityReport};
  } catch (e) {
    console.warn(e);
    throw e;
  }
}

export async function getColumLetterWhichisExactly(
  SpecialtyClinicWorksheet: SpecialtyClinicWorksheet,
  text: string,
  graphClient: Client
): Promise<
  | "A"
  | "B"
  | "C"
  | "D"
  | "E"
  | "F"
  | "G"
  | "H"
  | "I"
  | "J"
  | "K"
  | "L"
  | "M"
  | "N"
  | undefined
> {
  const theArray = [
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
  ];
  let headders: WorkbookRange = await graphClient
    .api(`${SpecialtyClinicWorksheet.URI}range(address='A1:M5')`)
    .get();
  const row = headders.text.findIndex((row: string | string[]) =>
    row.includes(text)
  );
  try {
    const col = headders.text[row].indexOf(text);
    let result = theArray[col];
    //@ts-ignore
    return result;
  } catch (error) {
    return undefined;
  }
}
